/**
 * SurveyHeader Component
 *
 * Displays and allows editing of the survey name with inline validation.
 * Used at the top of all survey creation/editing pages.
 *
 * ## Used By
 * - CustomSurvey
 * - EventRegistrationSurvey
 * - SatisfactionSurvey
 * - DonationSurvey
 *
 * ## Features
 * - **Display Mode**: Shows survey name with edit button
 * - **Edit Mode**: Inline text input for changing name
 * - **Form Integration**: Uses React Hook Form Controller
 * - **Validation**: Shows error messages below input
 * - **Auto-focus**: Input focuses automatically when entering edit mode
 * - **Keyboard Shortcuts**: Enter/Escape keys close edit mode
 * - **Blur Behavior**: Clicking outside closes edit mode
 *
 * ## How It Works
 * 1. Parent manages `showEditName` state (true/false)
 * 2. User clicks edit pen icon → parent sets showEditName=true
 * 3. Component switches to edit mode with TextInput
 * 4. User types → calls onNameChange on every keystroke
 * 5. User presses Enter/Escape or clicks "Update Name" → parent sets showEditName=false
 * 6. Component switches back to display mode
 *
 * ## Required Setup
 * This component MUST be used inside a FormProvider (React Hook Form):
 * ```tsx
 * <FormProvider {...methods}>
 *   <form>
 *     <SurveyHeader {...props} />
 *   </form>
 * </FormProvider>
 * ```
 *
 * The form must have a field named 'name' (or custom fieldName prop).
 */

import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../components/Button';
import { EditItemPenIcon } from '../../../../../components/svgs/icons/EditItemPenIcon';
import { TextInput } from '../../../../../components/TextInput';
import { successGreen } from '../../../../../styles/colors';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { bs } from '../../../../../styles/styles';
import { FormFieldErrorMessage } from '../FormFieldWithOptions';

const {
	spacing,
	typography: { fontSizes },
} = THEME;

/**
 * Props for SurveyHeader component
 */
interface SurveyHeaderProps {
	/**
	 * Current survey name to display
	 *
	 * Shown in display mode as <h4> heading.
	 * If empty/falsy, displays "Untitled Survey" as placeholder.
	 *
	 * Example: "Customer Satisfaction Survey 2024"
	 */
	surveyName: string;

	/**
	 * Controls whether edit mode is active
	 *
	 * When true: Shows TextInput for editing
	 * When false: Shows survey name with edit button
	 *
	 * Parent component manages this state:
	 * ```tsx
	 * const [showEditSurveyName, setShowEditSurveyName] = useState(false);
	 * <SurveyHeader
	 *   showEditName={showEditSurveyName}
	 *   onEditToggle={() => setShowEditSurveyName(!showEditSurveyName)}
	 * />
	 * ```
	 */
	showEditName: boolean;

	/**
	 * Handler called when toggling between display/edit modes
	 *
	 * Called when:
	 * - User clicks edit pen icon (enters edit mode)
	 * - User clicks "Update Name" button (exits edit mode)
	 * - User presses Enter or Escape keys (exits edit mode)
	 * - User clicks outside input (onBlur, exits edit mode)
	 *
	 * Typical implementation:
	 * ```tsx
	 * onEditToggle={() => setShowEditSurveyName(!showEditSurveyName)}
	 * ```
	 */
	onEditToggle: () => void;

	/**
	 * Handler called when name changes (on every keystroke)
	 *
	 * Called during edit mode as user types.
	 * Use this to update form state or trigger validation.
	 *
	 * Note: React Hook Form's Controller already updates the form field,
	 * this callback is for additional side effects.
	 *
	 * Example:
	 * ```tsx
	 * onNameChange={(name) => {
	 *   // Additional logic if needed
	 *   console.log('Name changed to:', name);
	 * }}
	 * ```
	 */
	onNameChange: (name: string) => void;

	/**
	 * Validation error message to display
	 *
	 * Shown below the input/name in red text.
	 * Typically from React Hook Form's field errors:
	 * ```tsx
	 * validationError={formState.errors?.name?.message}
	 * ```
	 *
	 * Common validations:
	 * - "Survey name is required"
	 * - "Survey name must be at least 3 characters"
	 * - "Survey name already exists"
	 */
	validationError?: string;

	/**
	 * Whether survey is being edited (vs created)
	 *
	 * Currently unused but reserved for future functionality.
	 * Could be used to show different UI for edit vs create.
	 */
	isEdit?: boolean;

	/**
	 * Legacy className prop (use headerClassName instead)
	 *
	 * Applied to the <header> element if headerClassName not provided.
	 * Kept for backwards compatibility.
	 *
	 * @deprecated Use headerClassName for clarity
	 */
	className?: string;

	/**
	 * Additional CSS class for the <header> element
	 *
	 * Applied in addition to default styles:
	 * ```tsx
	 * headerClassName={css(customStyles.surveyHeader)}
	 * ```
	 */
	headerClassName?: string;

	/**
	 * Additional CSS class for the title <h4> element
	 *
	 * Applied in addition to default title styles:
	 * ```tsx
	 * titleClassName={css(customStyles.largerTitle)}
	 * ```
	 */
	titleClassName?: string;

	/**
	 * Form field name for React Hook Form Controller
	 *
	 * @default "name"
	 *
	 * Must match the field name in your form schema.
	 * Only change if your form uses a different field name for survey name.
	 *
	 * Example with custom field:
	 * ```tsx
	 * fieldName="surveyTitle"
	 * ```
	 */
	fieldName?: string;
}

/**
 * SurveyHeader component implementation
 *
 * @example Basic usage
 * ```tsx
 * const [showEditSurveyName, setShowEditSurveyName] = useState(false);
 *
 * <FormProvider {...methods}>
 *   <form onSubmit={handleSubmit(onSubmit)}>
 *     <SurveyHeader
 *       surveyName={formValues.name}
 *       showEditName={showEditSurveyName}
 *       onEditToggle={() => setShowEditSurveyName(!showEditSurveyName)}
 *       onNameChange={(name) => console.log('Name changed:', name)}
 *       validationError={formState.errors?.name?.message}
 *     />
 *   </form>
 * </FormProvider>
 * ```
 *
 * @example With custom styling
 * ```tsx
 * <SurveyHeader
 *   surveyName={survey?.name || ''}
 *   showEditName={showEditSurveyName}
 *   onEditToggle={handleToggleEditName}
 *   onNameChange={handleNameChange}
 *   validationError={formState.errors?.name?.message}
 *   headerClassName={css(customStyles.stickyHeader)}
 *   titleClassName={css(customStyles.largerTitle)}
 * />
 * ```
 */
export const SurveyHeader: React.FC<SurveyHeaderProps> = ({
	surveyName,
	showEditName,
	onEditToggle,
	onNameChange,
	validationError,
	isEdit: _isEdit = false, // Prefixed to indicate intentionally unused
	className,
	headerClassName,
	titleClassName,
	fieldName = 'name',
}) => {
	// Get form control from FormProvider context
	// This allows the Controller to register the field with React Hook Form
	const { control } = useFormContext();

	// Check if we're in preview-only mode (viewing a template)
	const { isPreviewOnly } = usePreviewMode();

	return (
		<header
			// Complex className logic for backwards compatibility
			// Priority: headerClassName > className > default styles
			className={
				headerClassName
					? `${css(styles.headerInner)} ${headerClassName}`
					: className
						? `${css(styles.headerInner)} ${className}`
						: css(styles.headerInner)
			}
			data-testid='survey-header'
		>
			{/* Label for accessibility */}
			<label htmlFor='survey-name-input'>Survey Name:</label>

			{/* Display Mode - Shows survey name with edit button */}
			{!showEditName ? (
				<div className={css(bs.flex, bs.justifyStart, bs.gap2, bs.relative, styles.headerNameWrap)}>
					<div>
						{/* Survey Name Title */}
						<h4
							className={
								titleClassName
									? `${css(bs.truncateText, styles.headerTitle)} ${titleClassName}`
									: css(bs.truncateText, styles.headerTitle)
							}
						>
							{/* Show "Untitled Survey" if name is empty */}
							{surveyName || 'Untitled Survey'}
						</h4>
						{/* Validation errors shown below title */}
						<FormFieldErrorMessage errorMessage={validationError} />
					</div>

					{/* Edit Button - Green pen icon (hidden in preview mode) */}
					{!isPreviewOnly && (
						<Button
							type='button' // Not a form submission
							kind='custom' // Custom button styling
							className={css(styles.editPen)}
							onClick={onEditToggle} // Switch to edit mode
							data-testid='edit-name-btn'
							title='Edit survey name' // Tooltip on hover
							aria-label='Edit survey name' // Screen reader accessibility
						>
							<EditItemPenIcon fillColor={successGreen} />
						</Button>
					)}
				</div>
			) : (
				/* Edit Mode - Shows text input with Update button */
				<div className={css(bs.flex, bs.flexCenter, bs.gap2, styles.headerNameInputWrap)}>
					<div>
						{/* React Hook Form Controller - manages form field state */}
						<Controller
							name={fieldName} // Form field name (default: 'name')
							control={control} // Form control from FormProvider
							render={({ field: { ref, ...fieldProps } }) => {
								return (
									<TextInput
										{...fieldProps} // Spread field props (value, onChange, onBlur, name)
										id='survey-name-input'
										inputId='survey-name-input'
										placeholder='Enter survey name'
										onChange={e => {
											const value = e.target.value;
											// Update React Hook Form state
											fieldProps.onChange(value);
											// Call parent's onNameChange for additional side effects
											onNameChange(value);
										}}
										type='text'
										autoFocus // Automatically focus input when edit mode opens
										onBlur={() => {
											// Call field's onBlur for React Hook Form validation
											fieldProps.onBlur();
											// Exit edit mode when clicking outside
											onEditToggle();
										}}
										onKeyDown={(e: React.KeyboardEvent) => {
											// Keyboard shortcuts: Enter or Escape to exit edit mode
											if (e.key === 'Enter' || e.key === 'Escape') {
												onEditToggle();
											}
										}}
									/>
								);
							}}
						/>
						{/* Validation errors shown below input */}
						<FormFieldErrorMessage errorMessage={validationError} />
					</div>

					{/* Update Name Button - exits edit mode */}
					<Button
						type='button' // Not a form submission
						kind='link' // Link-styled button (less prominent)
						disabled={!surveyName} // Disabled if name is empty
						onClick={onEditToggle} // Exit edit mode
						data-testid='close-edit-btn'
					>
						Update Name
					</Button>
				</div>
			)}
		</header>
	);
};

/**
 * Styles for SurveyHeader component
 */
const styles = StyleSheet.create({
	headerInner: {
		paddingLeft: spacing.md, // Consistent left padding from design system
	},
	headerNameWrap: {
		height: 59, // Fixed height for consistent layout in display mode
	},
	headerTitle: {
		fontSize: fontSizes.lg, // Large font size from design system
		lineHeight: '22px', // Fixed line height (must be in px for cross-browser consistency)
		color: '#1a1a1a', // Dark gray text color
		margin: spacing.none, // Remove default heading margins
		paddingBottom: spacing.none, // Remove default heading padding
		maxWidth: 725, // Prevent extremely long names from breaking layout
		// Note: Truncate text handled by bs.truncateText class (adds ellipsis)
	},
	headerNameInputWrap: {
		minHeight: 50, // Minimum height for edit mode container
		marginBottom: spacing.sm, // Space below input when in edit mode
	},
	editPen: {
		position: 'absolute', // Positioned relative to headerNameWrap
		top: spacing.none, // Aligned to top
		right: -spacing.xl, // Positioned to the right with negative margin (outside container)
		// This places the edit button to the right of the survey name
	},
});

/**
 * Type exports for consumers
 */
export type { SurveyHeaderProps };
